import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { Inbox, CheckCircle2, XCircle } from "lucide-react";

/**
 * Approval Inbox — surfaces PRs awaiting the current user.
 * Used on both the Dashboard (compact=true) and Procurement page (compact=false).
 */
export default function ApprovalInboxCard({ compact = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // pr_id being acted on

  const load = async () => {
    setLoading(true);
    try {
      // Load PRs (procurement module) AND POs (any org) in parallel
      const [prsRes, pendingRes] = await Promise.allSettled([
        api.get("/procurement/prs/pending-for-me"),
        api.get("/approvals/pending"),
      ]);
      const prs = prsRes.status === "fulfilled" ? (prsRes.value.data?.prs || []) : [];
      const pos = pendingRes.status === "fulfilled" ? (pendingRes.value.data?.purchase_orders || []) : [];
      // Merge into a unified inbox — POs mapped to same shape as PRs for the card
      const poShaped = pos.map((p) => ({
        id: p.id,
        kind: "po",
        pr_number: p.po_number,
        total_estimated: p.grand_total,
        department: null,
        date: p.date,
        current_approval_level: 1,
        approval_chain_size: 1,
        pending_step: { approver_role: "approver" },
      }));
      const merged = [...prs.map((p) => ({ ...p, kind: "pr" })), ...poShaped];
      setRows(merged);
    } catch (e) {
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (pr, action) => {
    setBusy(pr.id);
    try {
      if (pr.kind === "po") {
        // POs — only support approve; reject not wired in the simple approvals API
        if (action === "approve") await api.post(`/purchase/orders/${pr.id}/approve`);
        else throw new Error("Rejection for POs is done from Purchases page");
        toast.success(`${pr.pr_number} approved`);
      } else {
        await api.post(`/procurement/prs/${pr.id}/${action}`);
        toast.success(`${pr.pr_number} ${action === "approve" ? "approved" : "rejected"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || `Failed to ${action}`);
    } finally { setBusy(null); }
  };

  const visible = compact ? rows.slice(0, 5) : rows;

  return (
    <div className="card-sharp p-5" data-testid="approval-inbox-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Inbox className="w-3 h-3" /> Approval inbox
          </div>
          <div className="font-heading text-lg mt-1" data-testid="approval-inbox-count">
            {loading ? "…" : `${rows.length} pending`}
          </div>
        </div>
        {compact && rows.length > 5 && (
          <Link to="/app/procurement" className="text-xs text-accent hover:underline" data-testid="approval-inbox-see-all">
            See all →
          </Link>
        )}
      </div>

      {!loading && rows.length === 0 && (
        <div className="text-xs text-muted-foreground py-6 text-center border border-dashed rounded-sm">
          Nothing awaiting your approval. Enjoy the calm.
        </div>
      )}

      {visible.length > 0 && (
        <div className="space-y-2">
          {visible.map((pr) => {
            const step = pr.pending_step || {};
            return (
              <div key={pr.id} className="border rounded-sm p-2.5 hover:bg-muted/40 transition-colors" data-testid={`inbox-pr-${pr.pr_number}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{pr.pr_number}</span>
                      <span className="text-[9px] font-mono uppercase text-muted-foreground">L{pr.current_approval_level}/{pr.approval_chain_size}</span>
                    </div>
                    <div className="text-xs mt-0.5 truncate">
                      <span className="tabular-nums font-medium">{fmtINR(pr.total_estimated)}</span>
                      <span className="text-muted-foreground"> · {pr.department || "—"} · {fmtDate(pr.date)}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      As <span className="font-mono">{step.approver_role || "—"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" disabled={busy === pr.id}
                            className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700"
                            onClick={() => act(pr, "approve")}
                            data-testid={`inbox-approve-${pr.pr_number}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === pr.id}
                            className="h-7 text-xs gap-1 text-red-500 hover:text-red-600"
                            onClick={() => act(pr, "reject")}
                            data-testid={`inbox-reject-${pr.pr_number}`}>
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
